import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Intent } from "@blueprintjs/core";
import { ShowHide } from "components/layout";
import { NotificationToaster } from "components/notifications";
import { EarnedRecognitionService } from "services/EarnedRecognitionService";

const landingPagePath = "/earned-recognition";

const enrolmentFlagByStream = {
    driver: "sendDriverData",
    maintenance: "sendMaintenanceData"
};

const gateStates = {
    loading: "loading",
    enrolled: "enrolled",
    blocked: "blocked"
};

// Mounts a KPI drilldown only once the account is confirmed as enrolled in the drilldown's Earned
// Recognition stream. The children are not mounted until then, so none of the drilldown's data
// requests are made for an account outside the stream. An account that is not enrolled is sent to
// the Earned Recognition landing page, which explains why. A failed enrolment check is not treated
// as "not enrolled": the user is told to refresh and stays on the page with nothing shown.
export function ErEnrolmentGate({ stream, children }) {
    const navigate = useNavigate();
    const [gateState, setGateState] = useState(gateStates.loading);

    useEffect(loadEnrolment, []);

    function loadEnrolment() {
        EarnedRecognitionService.getEarnedRecognition().then(
            (settings) => {
                const streamEnabled = settings[enrolmentFlagByStream[stream]] ?? false;

                if (streamEnabled) {
                    setGateState(gateStates.enrolled);
                } else {
                    navigate(landingPagePath, { replace: true });
                }
            },
            () => {
                NotificationToaster.show(Intent.DANGER, "Could not load Earned Recognition settings. Please refresh the page.");
                setGateState(gateStates.blocked);
            }
        );
    }

    return (
        <ShowHide
            evaluator={gateState === gateStates.enrolled}
            show={children}
            hide={(
                <ShowHide
                    evaluator={gateState === gateStates.loading}
                    show={(<h1 className="bp6-skeleton">Earned Recognition</h1>)}
                />
            )}
        />
    );
}

ErEnrolmentGate.propTypes = {
    stream: PropTypes.oneOf(Object.keys(enrolmentFlagByStream)).isRequired,
    children: PropTypes.node.isRequired
};
